import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';
import './StatisticsPage.css';

// Données fictives pour la démonstration
const demoOrdersData = {
  daily: [
    { name: '08 Avr', orders: 28, revenue: 845.50 },
    { name: '09 Avr', orders: 32, revenue: 1020.80 },
    { name: '10 Avr', orders: 30, revenue: 897.20 },
    { name: '11 Avr', orders: 35, revenue: 1245.60 },
    { name: '12 Avr', orders: 45, revenue: 1578.30 },
    { name: '13 Avr', orders: 52, revenue: 1890.40 },
    { name: '14 Avr', orders: 38, revenue: 1352.70 }
  ],
  weekly: [
    { name: 'Sem 1', orders: 195, revenue: 5982.30 },
    { name: 'Sem 2', orders: 210, revenue: 6348.90 },
    { name: 'Sem 3', orders: 187, revenue: 5540.80 },
    { name: 'Sem 4', orders: 245, revenue: 7824.50 }
  ],
  monthly: [
    { name: 'Jan', orders: 820, revenue: 24980.50 },
    { name: 'Fév', orders: 790, revenue: 23470.80 },
    { name: 'Mar', orders: 850, revenue: 26780.30 },
    { name: 'Avr', orders: 892, revenue: 28240.60 }
  ]
};

const demoRestaurantRevenue = [
  { name: "Le Gourmet", revenue: 12540.80 },
  { name: "Sushi Master", revenue: 9870.50 },
  { name: "Pizza Express", revenue: 11230.40 },
  { name: "Burger House", revenue: 7890.30 },
  { name: "Thai Delices", revenue: 6780.90 }
];

const demoPopularItems = [
  { name: "Pizza Margherita", orders: 245 },
  { name: "Burger Classic", orders: 210 },
  { name: "Sushi Mix", orders: 180 },
  { name: "Risotto aux Cèpes", orders: 150 },
  { name: "Pad Thai", orders: 135 }
];

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F'];

const StatisticsPage = () => {
  const [timeRange, setTimeRange] = useState('daily');
  const [ordersData, setOrdersData] = useState(demoOrdersData.daily);
  const [restaurantRevenue] = useState(demoRestaurantRevenue);
  const [popularItems] = useState(demoPopularItems);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Simuler un chargement de données depuis une API
  useEffect(() => {
    // Dans un cas réel, vous feriez un appel API ici
    // fetch(`/api/admin/statistics?timeRange=${timeRange}&start=${dateRange.startDate}&end=${dateRange.endDate}`)
    //   .then(response => response.json())
    //   .then(data => {
    //     setOrdersData(data.ordersData);
    //     setRestaurantRevenue(data.restaurantRevenue);
    //     setPopularItems(data.popularItems);
    //   });

    // Pour la démo, on utilise les données statiques
    setOrdersData(demoOrdersData[timeRange]);
  }, [timeRange, dateRange]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const handleDateChange = (event) => {
    setDateRange({
      ...dateRange,
      [event.target.name]: event.target.value
    });
  };

  const formatCurrency = (value) => {
    return `${value.toFixed(2)} €`;
  };

  // Calculer le total des commandes et des revenus pour la période sélectionnée
  const totalOrders = ordersData.reduce((sum, item) => sum + item.orders, 0);
  const totalRevenue = ordersData.reduce((sum, item) => sum + item.revenue, 0);

  return (
    <div className="admin-dashboard">
      <AdminSidebar />
      <div className="dashboard-content">
        <AdminHeader title="Statistiques" />
        
        <div className="statistics-container">
          <div className="statistics-header">
            <div className="time-range-selector">
              <select value={timeRange} onChange={handleTimeRangeChange}>
                <option value="daily">Quotidien</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuel</option>
              </select>
            </div>
            
            <div className="date-range-selector">
              <div className="date-input-group">
                <label>Du:</label>
                <input 
                  type="date" 
                  name="startDate" 
                  value={dateRange.startDate} 
                  onChange={handleDateChange}
                />
              </div>
              <div className="date-input-group">
                <label>Au:</label>
                <input 
                  type="date" 
                  name="endDate" 
                  value={dateRange.endDate} 
                  onChange={handleDateChange}
                />
              </div>
              <button className="apply-filter">Appliquer</button>
            </div>
          </div>
          
          <div className="stats-summary">
            <div className="stats-card">
              <h3>Total des commandes</h3>
              <div className="stats-value">{totalOrders}</div>
            </div>
            <div className="stats-card">
              <h3>Revenu total</h3>
              <div className="stats-value">{formatCurrency(totalRevenue)}</div>
            </div>
            <div className="stats-card">
              <h3>Panier moyen</h3>
              <div className="stats-value">
                {formatCurrency(totalRevenue / totalOrders)}
              </div>
            </div>
          </div>
          
          <div className="charts-container">
            <div className="chart-card wide-card">
              <h3>Évolution des commandes et revenus</h3>
              <div className="chart-area">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={ordersData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value, name) => [
                      name === 'revenue' ? formatCurrency(value) : value,
                      name === 'revenue' ? 'Revenu' : 'Commandes'
                    ]} />
                    <Legend />
                    <Line 
                      yAxisId="left" 
                      type="monotone" 
                      dataKey="orders" 
                      name="Commandes" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                    />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Revenu" 
                      stroke="#82ca9d" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="chart-card">
              <h3>Revenu par restaurant</h3>
              <div className="chart-area">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={restaurantRevenue} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenu" fill="#8884d8">
                      {restaurantRevenue.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="chart-card">
              <h3>Articles les plus commandés</h3>
              <div className="chart-area">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={popularItems}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="orders"
                      nameKey="name"
                      label={({ name, orders }) => `${name} (${orders})`}
                    >
                      {popularItems.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [`${value} commandes`, props.payload.name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          <div className="export-section">
            <button className="export-button">
              Exporter en PDF
            </button>
            <button className="export-button">
              Exporter en Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;